import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getSystemTableColumnOptions = async (token, systemTableColumnId) => {
  const url = systemTableColumnId
    ? `admin/system_table_columns/${systemTableColumnId}/system_table_column_options`
    : 'admin/system_table_column_options';
  const data = await apiGet({ url, token });
  return data;
};

const getSystemTableColumnOption = async (token, id) => {
  const url = `admin/system_table_column_options/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createSystemTableColumnOption = async (token, systemTableColumnId, params) => {
  const url = `admin/system_table_columns/${systemTableColumnId}/system_table_column_options`;
  const data = await apiPost({ url, token, objectName: 'system_table_column_option', params });
  return data;
};

const updateSystemTableColumnOption = async (token, id, params) => {
  const url = `admin/system_table_column_options/${id}`;
  const data = await apiPatch({ url, token, objectName: 'system_table_column_option', params });
  return data;
};

const deleteSystemTableColumnOption = async (token, id) => {
  const url = `admin/system_table_column_options/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminSystemTableColumnOptions({ systemTableColumnId, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSystemTableColumnOptions(token, systemTableColumnId);
  const queryKey = systemTableColumnId
    ? ['admin-system-table-column-options', systemTableColumnId]
    : 'admin-system-table-column-options';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminSystemTableColumnOption({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSystemTableColumnOption(token, id);
  const queryKey = ['admin-system-table-column-option', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminSystemTableColumnOption({ systemTableColumnId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createSystemTableColumnOption(token, systemTableColumnId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-system-table-column-options');
        queryClient.invalidateQueries(['admin-system-table-column-options', systemTableColumnId]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminSystemTableColumnOption({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateSystemTableColumnOption(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-system-table-column-options');
        queryClient.invalidateQueries(['admin-system-table-column-option', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminSystemTableColumnOption({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteSystemTableColumnOption(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-system-table-column-options');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
